import { type FC, useState } from "react";
import { useForm } from "react-final-form";
import { useTranslation } from "react-i18next";
import { Box, Stack, Button, Menu, MenuItem, Typography } from "@mui/material";
import { SelectObjectDialog } from "./SelectObjectDialog";
import { SelectedObjectItem } from "./SelectedObjectItem";
import type { ObjectItem, SelectObjectTypeConfig } from "../types";

interface SelectObjectFieldProps {
  targets: Record<string, SelectObjectTypeConfig>;
  typeField?: string;
  idField?: string;
  titleField?: string;
  urlField?: string;
  getTargetOption?: (values: Record<string, unknown>) => string | undefined;
  name?: string;
  allValues?: Record<string, unknown>;
  editData?: Record<string, unknown>;
}

const asString = (value: unknown): string | undefined =>
  typeof value === "string" && value ? value : undefined;

/**
 * Picker of a link target: one "Select" button with a menu of target kinds
 * (page, article, video...), a search dialog per kind. Writes targetType and
 * targetId (plus title and URL for display) into the form.
 */
export const SelectObjectField: FC<SelectObjectFieldProps> = ({
  targets,
  typeField = "targetType",
  idField = "targetId",
  titleField = "targetTitle",
  urlField = "targetUrl",
  getTargetOption,
  allValues = {},
}) => {
  const { t } = useTranslation();
  const form = useForm();

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);

  // Option picked in this session: the saved targetType alone does not tell
  // a video from a stream or a collection.
  const [pickedOption, setPickedOption] = useState<string | null>(null);
  const [dialogOption, setDialogOption] = useState<string | null>(null);

  const targetType = asString(allValues[typeField]);
  const targetId = asString(allValues[idField]);

  const selectedOption = (() => {
    if (!targetType || !targetId) return undefined;
    if (pickedOption && targets[pickedOption]?.targetType === targetType) {
      return pickedOption;
    }
    const resolved = getTargetOption?.(allValues);
    if (resolved && targets[resolved]) return resolved;
    return Object.keys(targets).find(
      (key) => targets[key].targetType === targetType
    );
  })();

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleSelectOption = (option: string) => {
    setDialogOption(option);
    setMenuAnchorEl(null);
  };

  const handleSelectObject = (item: ObjectItem) => {
    if (!dialogOption) return;
    const config = targets[dialogOption];
    form.batch(() => {
      form.change(typeField, config.targetType);
      form.change(idField, item.id);
      form.change(titleField, item.title ?? null);
      form.change(urlField, asString(item.url) ?? asString(item.path) ?? null);
    });
    setPickedOption(dialogOption);
    setDialogOption(null);
  };

  const dialogConfig = dialogOption ? targets[dialogOption] : undefined;
  const selectedConfig = selectedOption ? targets[selectedOption] : undefined;

  return (
    <Box sx={{ position: "relative" }} py={1}>
      <Stack direction="row" alignItems="center" gap={1} width={1}>
        <Box
          sx={{
            flex: "1 1 0",
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          {targetId ? (
            <SelectedObjectItem
              item={{
                id: targetId,
                title: asString(allValues[titleField]),
                path: asString(allValues[urlField]),
              }}
              config={selectedConfig}
            />
          ) : (
            <Box sx={{ minWidth: 0, overflow: "hidden" }}>
              <Typography variant="body1" noWrap>
                {t("shared.selectObject.empty", {
                  defaultValue: "Объект не выбран",
                })}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {t("shared.selectObject.emptyHint", {
                  defaultValue: "Выберите объект на который будет вести ссылка",
                })}
              </Typography>
            </Box>
          )}
        </Box>

        <Button
          variant="contained"
          color="primary"
          style={{ marginLeft: "auto", flex: "0 0 auto" }}
          onClick={handleOpenMenu}
        >
          {targetId
            ? t("shared.selectObject.change", { defaultValue: "Изменить" })
            : t("shared.selectObject.select", { defaultValue: "Выбрать" })}
        </Button>
      </Stack>

      <Menu
        id="select-object-menu"
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onClose={() => setMenuAnchorEl(null)}
      >
        {Object.entries(targets).map(([option, config]) => (
          <MenuItem key={option} onClick={() => handleSelectOption(option)}>
            {config.label ?? option}
          </MenuItem>
        ))}
      </Menu>

      {dialogConfig && (
        <SelectObjectDialog
          config={dialogConfig}
          onSelectObject={handleSelectObject}
          onClose={() => setDialogOption(null)}
        />
      )}
    </Box>
  );
};
